import { type FormEvent, useState } from 'react';
import { api } from '../../lib/api';
import { KIND_LABEL, type ProjectRef, type Site, type SiteKind, errText, pickSite } from './common';

const SLUG = /^[a-z0-9-]{2,40}$/;

// „+ Új weboldal”: csak az alapok (név, domain, fajta, projekt, port); a többi a részlet Beállítások fülén.
export function NewSiteModal({ projects, onClose, onCreated }: { projects: ProjectRef[]; onClose: () => void; onCreated: (site: Site | null) => void }) {
  const [name, setName] = useState('');
  const [domains, setDomains] = useState('');
  const [kind, setKind] = useState<SiteKind>('static');
  const [projectId, setProjectId] = useState('');
  const [port, setPort] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const domainList = domains.split(/[\s,]+/).map((d) => d.trim().toLowerCase()).filter(Boolean);
  const slugOk = SLUG.test(name);
  const needsPort = kind !== 'static';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!slugOk) { setErr('A név 2–40 karakter: kisbetű, szám, kötőjel.'); return; }
    if (!domainList.length) { setErr('Legalább egy domain kell.'); return; }
    if (needsPort && !Number(port)) { setErr('Proxy és konténer fajtánál a port kötelező.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api<unknown>('/api/n4/sites', { method: 'POST', body: JSON.stringify({ name, domains: domainList, kind, project_id: projectId ? Number(projectId) : null, upstream_port: port ? Number(port) : null }) });
      onCreated(pickSite(r));
    } catch (ex) { setErr(errText(ex, 'Nem sikerült létrehozni')); } finally { setBusy(false); }
  };

  return (
    <div className="a-overlay fixed inset-0 z-40 flex items-start justify-center overflow-auto p-4 pt-16" onClick={onClose}>
      <div className="a-panel a-pad a-modal-in w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[20px] text-neutral-100">Új weboldal</h3>
          <span role="button" tabIndex={0} className="cursor-pointer px-2 text-neutral-500 hover:text-neutral-200" onClick={onClose}>✕</span>
        </div>
        <form onSubmit={(e) => void submit(e)} className="a-site-form">
          <label className="a-site-field"><span>Név (azonosító)</span>
            <input className="a-input" value={name} onChange={(e) => setName(e.target.value.toLowerCase())} placeholder="pl. forum" autoFocus />
            <small className={name && !slugOk ? 'bad' : ''}>Kisbetű, szám, kötőjel (2–40). Ebből lesz a mappa és a konténer neve.</small></label>
          <label className="a-site-field"><span>Domain(ek)</span>
            <input className="a-input" value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="forum.naryan.net, www.forum.naryan.net" />
            <small>Vesszővel vagy szóközzel elválasztva.</small></label>
          <div className="a-site-field"><span>Fajta</span>
            <div className="a-site-seg">{(['static', 'proxy', 'container'] as SiteKind[]).map((k) => (
              <button key={k} type="button" className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>{KIND_LABEL[k]}</button>
            ))}</div>
            <small>{kind === 'static' ? 'Fájlok kiszolgálása a www-mappából (SPA, tömörítés, fejlécek).' : kind === 'proxy' ? 'Egy helyben futó szolgáltatás (pl. systemd-egység) a megadott porton.' : 'Docker-konténer APM-recepttel (a recept a Beállításokban).'}</small></div>
          <div className="a-site-fields">
            <label className="a-site-field"><span>Projekt</span>
              <select className="a-input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">— nincs —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></label>
            <label className="a-site-field"><span>Port{needsPort ? '' : ' (nem kell)'}</span>
              <input className="a-input" type="number" min={1} max={65535} value={port} onChange={(e) => setPort(e.target.value)} placeholder={needsPort ? 'pl. 3900' : '—'} disabled={!needsPort} /></label>
          </div>
          {err && <p className="a-site-msg bad">{err}</p>}
          <div className="flex gap-2"><button type="submit" className="a-btn solid" disabled={busy}>{busy ? 'Létrehozás…' : 'Létrehozás'}</button><button type="button" className="a-btn ghost" onClick={onClose}>Mégse</button></div>
        </form>
      </div>
    </div>
  );
}
